import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Logo } from "@/components/ui/Logo";

const navLinks: { href: string; label: string; isRoute?: boolean; highlight?: boolean }[] = [
  { href: "#features", label: "Features" },
  { href: "#how-it-works", label: "How It Works" },
  { href: "#pricing", label: "Pricing" },
  { href: "#contact", label: "Contact" },
];

interface NavbarProps {
  showAuthButtons?: boolean;
  isHomePage?: boolean;
  theme?: 'light' | 'dark';
  onToggleTheme?: () => void;
}

export function Navbar({ showAuthButtons = true, isHomePage = false, theme, onToggleTheme }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const location = useLocation();

  const isLandingPage = location.pathname === "/" || location.pathname === "/home";

  // Link colors adapt to the surface: ink/steel-dark on the paper hero (theme=light),
  // paper-tinted on a dark surface (theme=dark). Fixes invisible nav on the paper landing.
  const isLight = theme === 'light';
  const linkColor = isLight ? '#5F6B7C' : 'rgba(232, 230, 240, 0.8)';
  const linkColorLeave = isLight ? '#5F6B7C' : 'rgba(232, 230, 240, 0.7)';
  const linkHover = isLight ? '#12263A' : '#E8E6F0';
  const navFont = "'Archivo', system-ui, sans-serif";

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      setIsScrolled(currentY > 80);

      // Hide on scroll down, reveal on scroll up
      if (currentY > lastScrollY.current && currentY > 200) {
        setIsHidden(true);
      } else {
        setIsHidden(false);
      }
      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: isHidden ? -100 : 0 }}
      transition={{ duration: 0.35, ease: "easeInOut" }}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={isScrolled ? {
        background: theme === 'dark' ? 'rgba(6, 6, 15, 0.85)' : 'var(--lp-nav-bg)',
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderBottom: theme === 'dark' ? '1px solid rgba(232, 230, 240, 0.06)' : '1px solid var(--lp-nav-border)',
      } : {}}
    >
      <div className="container-wide">
        <nav className="flex items-center justify-between h-20 md:h-28">
          {/* Logo */}
          <Link to={isHomePage ? "/tool" : "/"} className="flex items-center">
            <Logo variant="icon-wordmark" className={`h-16 md:h-24${theme === 'light' ? ' !text-[#1a1a1a]' : ''}`} inverted={!theme || theme === 'dark'} />
          </Link>

          {/* Desktop nav links */}
          {isLandingPage && (
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) =>
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    className={`text-lg font-medium transition-colors duration-200 tracking-wide ${
                      link.highlight
                        ? 'text-[#34D399] hover:text-[#6EE7B7]'
                        : 'text-[rgba(232,230,240,0.8)] hover:text-[#E8E6F0]'
                    }`}
                    style={{ fontFamily: "'General Sans', sans-serif" }}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    className="text-lg font-medium transition-colors duration-200 tracking-wide"
                    style={{ color: linkColor, fontFamily: navFont }}
                    onMouseEnter={(e) => ((e.target as HTMLAnchorElement).style.color = linkHover)}
                    onMouseLeave={(e) => ((e.target as HTMLAnchorElement).style.color = linkColorLeave)}
                  >
                    {link.label}
                  </a>
                )
              )}
            </div>
          )}

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            {/* Theme toggle (landing page only) */}
            {isLandingPage && onToggleTheme && (
              <button
                onClick={onToggleTheme}
                className="p-2 rounded-lg transition-colors"
                style={{ color: "var(--color-text-muted)" }}
                aria-label="Toggle theme"
                onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.color = "var(--color-text)")}
                onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.color = "var(--color-text-muted)")}
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            )}

            {isHomePage ? (
              <Link to="/tool">
                <button
                  className="lp-btn-primary px-7 py-3.5 rounded-xl text-base font-semibold transition-all duration-200"
                  style={{ fontFamily: "'General Sans', sans-serif" }}
                >
                  Back to App
                </button>
              </Link>
            ) : showAuthButtons ? (
              <>
                <Link
                  to="/login"
                  className="px-5 py-3 text-lg font-medium transition-colors"
                  style={{ color: linkColor, fontFamily: navFont }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = linkHover)}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = linkColorLeave)}
                >
                  Sign In
                </Link>
                <Link to="/register">
                  <button
                    className="lp-btn-primary px-7 py-3.5 rounded-xl text-base font-semibold transition-all duration-200"
                    style={{ fontFamily: "'General Sans', sans-serif" }}
                  >
                    Start Free Trial
                  </button>
                </Link>
              </>
            ) : null}
          </div>

          {/* Mobile controls */}
          <div className="md:hidden flex items-center gap-1">
            {isLandingPage && onToggleTheme && (
              <button
                onClick={onToggleTheme}
                className="p-2 rounded-lg transition-colors"
                style={{ color: "var(--color-text-muted)" }}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            )}
            <button
              className="p-2 transition-colors"
              style={{ color: "var(--color-text-muted)" }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="md:hidden overflow-hidden"
              style={{
                background: theme === 'dark' ? 'rgba(6, 6, 15, 0.95)' : 'var(--lp-nav-bg)',
                backdropFilter: "blur(20px)",
                borderTop: theme === 'dark' ? '1px solid rgba(232, 230, 240, 0.06)' : '1px solid var(--lp-nav-border)',
              }}
            >
              <div className="flex flex-col gap-1 py-6 px-2">
                {isLandingPage &&
                  navLinks.map((link) =>
                    link.isRoute ? (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`px-4 py-3 text-base font-medium rounded-xl transition-all ${
                          link.highlight ? 'text-[#34D399]' : ''
                        }`}
                        style={!link.highlight ? { color: "var(--color-text-muted)", fontFamily: "'General Sans', sans-serif" } : { fontFamily: "'General Sans', sans-serif" }}
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        key={link.href}
                        href={link.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="px-4 py-3 text-base font-medium rounded-xl transition-all"
                        style={{ color: "var(--color-text-muted)", fontFamily: "'General Sans', sans-serif" }}
                      >
                        {link.label}
                      </a>
                    )
                  )}
                <div className="flex flex-col gap-3 mt-4 px-2">
                  {isHomePage ? (
                    <Link to="/tool" onClick={() => setIsMobileMenuOpen(false)}>
                      <button className="lp-btn-primary w-full py-3 rounded-xl font-semibold">
                        Back to App
                      </button>
                    </Link>
                  ) : showAuthButtons ? (
                    <>
                      <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>
                        <button className="lp-btn-outline w-full py-3 rounded-xl font-medium">
                          Sign In
                        </button>
                      </Link>
                      <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>
                        <button className="lp-btn-primary w-full py-3 rounded-xl font-semibold">
                          Start Free Trial
                        </button>
                      </Link>
                    </>
                  ) : null}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
